import React, { useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

/**
 * EmailBodyEditor — the shared editor for anything SENT AS EMAIL (campaign
 * steps, signatures, one-off composers). Deliberately restricted to an
 * email-safe subset (bold, italic, underline, lists, link) — NOT the full
 * notes toolbar — because rich formatting (headers, colors, fonts, tables)
 * breaks in Outlook and trips spam filters. Output is HTML.
 *
 * NOT for SMS or LinkedIn InMail (those are plain text — use a <textarea>).
 * NOT for internal notes (those use the fuller NotesPanel/MappingNotes editor).
 */
export default function EmailBodyEditor({ value, onChange, placeholder, minHeight = 260 }) {
  const quillRef = useRef(null);

  const modules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean'],
    ],
  }), []);

  const formats = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

  return (
    <div className="email-body-editor border rounded overflow-hidden">
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ''}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
      <style>{`
        .email-body-editor .ql-toolbar { border: none; border-bottom: 1px solid #e5e7eb; background: #f9fafb; }
        .email-body-editor .ql-container { border: none; font-family: inherit; font-size: 0.875rem; }
        /* Taller default; user can drag the bottom-right handle to grow it. */
        .email-body-editor .ql-editor {
          min-height: ${minHeight}px;
          max-height: 70vh;
          height: ${minHeight}px;
          overflow-y: auto;
          resize: vertical;
        }
        .email-body-editor .ql-editor::-webkit-scrollbar { width: 10px; }
        .email-body-editor .ql-editor::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 5px; border: 2px solid transparent; background-clip: content-box; }
        .email-body-editor .ql-editor::-webkit-scrollbar-thumb:hover { background: #9ca3af; background-clip: content-box; }
        .email-body-editor .ql-editor::-webkit-scrollbar-track { background: transparent; }
        [data-theme="dark"] .email-body-editor .ql-toolbar { background: #1f2937; border-color: #374151; }
        [data-theme="dark"] .email-body-editor .ql-container { color: #e5e7eb; }
        [data-theme="dark"] .email-body-editor .ql-editor.ql-blank::before { color: #6b7280; }
        [data-theme="dark"] .email-body-editor .ql-editor::-webkit-scrollbar-thumb { background: #4b5563; background-clip: content-box; }
      `}</style>
    </div>
  );
}

// Insert a merge-field token at the current cursor of a quill ref.
export function insertEmailToken(quillRef, token) {
  const editor = quillRef?.current?.getEditor?.();
  if (!editor) return;
  const range = editor.getSelection(true);
  const at = range ? range.index : editor.getLength();
  editor.insertText(at, `{{${token}}}`, 'user');
  editor.setSelection(at + token.length + 4, 0);
}
